import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  apiCallBegan,
  APICallMethods
} from '@gjensidige/se-redux-api-middleware';
import { useAppDispatch, useAppSelector } from '../hooks/appHooks';
import { API_OPERATIONS } from '../lib/constants/appConstants';
import API_ENDPOINTS from '../lib/constants/endpoints';
import {
  getModifiedInputs,
  getSelectedPackages,
  onApiError,
  onApiSuccess,
  updateLoadingStatus
} from '../store/slices/offerSlice';
import tracker from '../tracking';
import { FooterButtons } from '../components/Common/FooterButtons/FooterButtons';
import { Package } from '../interfaces/Types';
import { APIHelper } from '../utils/ApiHelper';
import { AppConstants } from '../utils/AppConstants';
import { RoutingHelper } from '../utils/RoutingHelper';
import { TrackingHelper } from '../utils/TrackingHelper';

interface IFooterContainerProps {
  shouldDisplayGoBackButton?: boolean;
  isNextButtonDisabled?: boolean;
}

export const FooterContainer: React.FC<IFooterContainerProps> = (
  props: IFooterContainerProps
) => {
  const navigate = useNavigate();
  const locationName = useLocation();
  const dispatch = useAppDispatch();
  const productName = useAppSelector((state) => state.insurance.offer.productName);
  const productNumber = useAppSelector((state) => state.insurance.offer.productCode);
  const addedInputs = useAppSelector((state) => state.insurance.additionalInputs);
  const selectedPackages = useAppSelector(getSelectedPackages);
  const modifiedInputs = useAppSelector(getModifiedInputs);
  const selectedInsurancePackage = selectedPackages.find((packageItem: Package) => !packageItem.isPlusPackage);
  const isPlusPacketSelected = selectedPackages.some((packageItem: Package) => packageItem.isPlusPackage);
  const selectedAdditionalPackages = selectedPackages.filter((packageItem: Package) => packageItem.isPlusPackage);
  const pricingQuestions = useAppSelector((state) => state.insurance.pricingQuestions);
  const offerInfo = useAppSelector((state) => state.insurance.offer);

  const handleNextButtonClick = async () => {

    const nextPageUrl = RoutingHelper.resolveNextPageUrlPath(
      productName,
      offerInfo.policyNumber,
      offerInfo.customerNumber,
      productNumber,
      offerInfo.coupon.couponCode,
      offerInfo.coupon.couponDiscountPercentage
    );
    if (RoutingHelper.shouldSaveOfferBeforeRoute(locationName.pathname)) {
      dispatch(updateLoadingStatus('updating'));
      saveSelections(API_OPERATIONS.SAVE_OFFER);
    } else {
      //Saving selections in session for page reload fix
      await APIHelper.saveSelectionsInSession(
        productName,
        pricingQuestions,
        addedInputs
      );
      if (
        RoutingHelper.shouldUpdateOffer(locationName.pathname, modifiedInputs)
      ) {
        await updateOffer();
      } else {
        navigatePage(nextPageUrl);
      }
    }
    if (
      TrackingHelper.shouldTrackInsuranceSelections(productName, nextPageUrl)
    ) {
      trackSelectionScenario(productName);
    }
  };

  const saveSelections = (apiOperation: API_OPERATIONS) => {
    dispatch(updateLoadingStatus('updating'));
    dispatch(
      apiCallBegan({
        init: APIHelper.getSaveOfferPayload(
          offerInfo,
          selectedInsurancePackage,
          selectedAdditionalPackages,
          isPlusPacketSelected,
          productName
        ),
        url: API_ENDPOINTS.saveOfferURL,
        apiOperation,
        method: APICallMethods.POST_JSON,
        onStart: '',
        onSuccess: onApiSuccess.type,
        onError: onApiError.type
      })
    );
  };

  const updateOffer = async () => {
    dispatch(updateLoadingStatus('updating'));
    dispatch(
      apiCallBegan({
        init: APIHelper.getBatchUpdatePayload(
          offerInfo,
          modifiedInputs,
          addedInputs
        ),
        url: API_ENDPOINTS.updateOfferURL,
        apiOperation: API_OPERATIONS.UPDATE_INSURANACE_OFFER,
        method: APICallMethods.POST_JSON,
        onStart: '',
        onSuccess: onApiSuccess.type,
        onError: onApiError.type
      })
    );
  };

  const navigatePage = (urlToRedirect: string) => {
    if (urlToRedirect) {
      if (RoutingHelper.isRoutingToPayment(locationName.pathname)) {
        dispatch(updateLoadingStatus('updating'));
        window.location.href = urlToRedirect;
      } else {
        navigate(urlToRedirect);
        window.scrollTo(0, 0);
      }
    }
  };

  const trackSelectionScenario = (productName: string) => {
    const { totalPrice, selectedPackageNames, selectedPackageYearlyPrices } =
      TrackingHelper.getEcommerceInfoToTrack(selectedPackages);
    const ecommPayload = {
      event_name: 'cart_add',
      application_name: 'webshop',
      application_module: 'offer',
      application_partner: 'gjensidige',
      application_version: '2.0',
      business_area: 'insurance',
      customer_segment: 'private',
      page_pathname: `/apps/${tracker.options.application_name}/${
        tracker.options.application_module
      }/${TrackingHelper.getScenarioNameFromProduct(
        productName
      )}/${TrackingHelper.getScenarioStepForInsuranceSelection(productName)}`,
      page_title: 'webshop-frontend-se',
      product_sku: [productNumber],
      product_variant_name: [TrackingHelper.getProductVariant(productName, selectedPackageNames, pricingQuestions)],
      product_variant_quantity: [`${selectedPackageNames.length}`],
      product_variant_total: [selectedPackageYearlyPrices.join(';')],
      product_name: TrackingHelper.getProductListToTrack(productName),
      product_brand: ['insurance'],
      product_category: [TrackingHelper.getProductCategoryToTrack(productName)],
      product_unit_price: [`${totalPrice ? totalPrice : 0}`],
      product_quantity: ['1'],
      scenario_name: TrackingHelper.getScenarioNameFromProduct(productName),
      scenario_step:
        TrackingHelper.getScenarioStepForInsuranceSelection(productName),
      customer_id: offerInfo.customerNumber || ''
    };
    tracker.trackView(ecommPayload);
  };

  const handleCancelButtonClick = () => {
    navigatePage(RoutingHelper.resolveFirstPageUrlPath(productName));
  };

  const handleGoBackButtonClick = () => {
    const previousPageUrl = RoutingHelper.resolvePreviousPageUrlPath(
      productName,
      offerInfo.policyNumber,
      offerInfo.customerNumber,
      productNumber,
      offerInfo.coupon.couponCode,
      offerInfo.coupon.couponDiscountPercentage
    );
    if (previousPageUrl) {
      navigate(previousPageUrl);
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <FooterButtons
        nextButtonLabel={AppConstants.getAppText('webshop.next.button.label')}
        isNextButtonDisabled={!!props.isNextButtonDisabled}
        goBackButtonLabel={AppConstants.getAppText(
          'webshop.cancel.popin.goBack.button'
        )}
        cancelButtonLabel={AppConstants.getAppText(
          'webshop.cancel.button.label'
        )}
        cancelButtonModalTitle={AppConstants.getAppText(
          'webshop.cancel.popin.title'
        )}
        cancelButtonModalContent={AppConstants.getAppText(
          'webshop.cancel.popin.description'
        )}
        shouldDisplayGoBackButton={props.shouldDisplayGoBackButton}
        shouldDisplayCancelAsButton
        nextButtonAction={handleNextButtonClick}
        goBackButtonAction={handleGoBackButtonClick}
        cancelButtonAction={handleCancelButtonClick}
        continueButtonLabel={AppConstants.getAppText(
          'webshop.cancel.popin.continueBuy.button'
        )}
      />
    </>
  );
};
